/**
 * Single source of truth for zone (Yelo) price calculation.
 *
 * Add Booking is the reference implementation — Add Quotation must produce the
 * exact same net price for the same route/class, so both screens call these
 * helpers instead of keeping their own copy of the km-range loop.
 */

type AnyPricing = any;

const toNumber = (value: any) =>
  typeof value === "number" ? value : Number(value ?? 0);

/**
 * Distance (trip) component of the net price, derived from the class' km ranges.
 *
 * Rules (in order):
 *  1. If a fixed-price slab *contains* the distance, that slab's price is the
 *     whole distance charge — no other range contributes.
 *  2. Otherwise the largest fixed slab the distance has already passed becomes
 *     the base charge, and `baseEndKm` marks the km already paid for.
 *  3. Per-km ranges bill only the kilometres between `max(startKm, baseEndKm)`
 *     and `min(distance, endKm)`.
 *  4. Plus-km ranges bill everything above `max(plusKm, baseEndKm)`.
 */
export const calculateKmRangeNetPrice = (
  pricing: AnyPricing,
  distance: number | string
): number => {
  const ranges = Array.isArray(pricing?.kmRangePrices)
    ? pricing.kmRangePrices
    : [];

  const d = toNumber(distance);
  let netto = 0;

  // If multiple fixed-price slabs exist, pick only the one that contains the distance
  const fixedMatch = ranges.find((item: any) => {
    const r = item?.kmRange_id || {};
    return r.isFixedPrice && d >= r.startKm && d <= r.endKm;
  });

  if (fixedMatch) return netto + fixedMatch.price;

  // Fall back to per-km and plus-km ranges
  // 1) Use the last fixed-price slab as base if distance exceeds it
  let baseEndKm = 0;
  const fixedBaseCandidates = ranges.filter((item: any) => {
    const r = item?.kmRange_id || {};
    return r.isFixedPrice && d > r.endKm;
  });
  if (fixedBaseCandidates.length > 0) {
    const lastFixed = fixedBaseCandidates.reduce((prev: any, curr: any) => {
      const prevEnd = prev?.kmRange_id?.endKm ?? -Infinity;
      const currEnd = curr?.kmRange_id?.endKm ?? -Infinity;
      return currEnd > prevEnd ? curr : prev;
    });
    baseEndKm = lastFixed?.kmRange_id?.endKm ?? 0;
    netto += lastFixed?.price ?? 0;
  }

  // 2) Apply per-km and plus-km only for distance beyond baseEndKm
  for (const item of ranges) {
    const range = item.kmRange_id;

    // Normal per km ranges (51–100, 101–200, 201–400, etc.)
    if (!range.isFixedPrice && !range.isPlusKm) {
      if (d > range.startKm) {
        const lower = Math.max(range.startKm, baseEndKm);
        const upper = Math.min(d, range.endKm);
        const kmInRange = Math.max(0, upper - lower);
        netto += kmInRange * item.price;
      }
    }

    // Plus Km (e.g., 401+)
    else if (range.isPlusKm) {
      const threshold = Math.max(range.plusKm, baseEndKm);
      if (d > threshold) {
        const kmAbove = d - threshold;
        netto += kmAbove * item.price;
      }
    }
  }

  return netto;
};

/**
 * Duration (hourly) component of the net price.
 *
 * Prefers the tiered `hourly_rates`, but falls back to the flat `per_hour_rate`
 * when the tiers were never filled in. That fallback is not an edge case: the
 * tiered rates were added after `per_hour_rate` and most existing rows were
 * never backfilled, so without it those classes price at exactly 0.00.
 */
export const calculateHourlyNetPrice = (
  pricing: AnyPricing,
  duration: number | string
): number => {
  const hours = toNumber(duration);
  const rates = pricing?.hourly_rates || {};

  const upto_8 = toNumber(rates.upto_8);
  const nine_to_12 = toNumber(rates.nine_to_12);
  const thirteen_to_24 = toNumber(rates.thirteen_to_24);

  if (upto_8 <= 0) return hours * toNumber(pricing?.per_hour_rate);

  let netto = 0;

  if (hours <= 8) {
    netto += hours * upto_8;
  } else if (hours <= 12) {
    netto += upto_8 * 8;
    netto += (hours - 8) * nine_to_12;
  } else {
    // No upper bound on the top tier — bounding it at 24 made any longer
    // booking fall through every branch and return 0.
    netto += upto_8 * 8;
    netto += nine_to_12 * 4;
    netto += thirteen_to_24 * (hours - 12);
  }

  return netto;
};

/**
 * Full net (pre-tax) price for a zone-priced booking: airport pickup surcharge
 * plus either the hourly or the distance component.
 */
export const calculateZoneNetPrice = ({
  pricing,
  distance,
  duration,
  hourly,
  airport,
  specialPercent,
}: {
  pricing: AnyPricing;
  distance?: number | string;
  duration?: number | string;
  hourly?: boolean;
  airport?: boolean;
  /**
   * Admin-defined special price for this (zone, class) at the trip's pickup
   * time, 1-100. Taken as a parameter rather than applied at each call site so
   * a screen cannot forget it.
   */
  specialPercent?: number;
}): number => {
  let netto = 0;

  if (airport) netto += pricing?.extra?.airport_pickup_cost ?? 0;

  if (hourly) netto += calculateHourlyNetPrice(pricing, duration ?? 0);
  else netto += calculateKmRangeNetPrice(pricing, distance ?? 0);

  // Last, on the whole net — including the airport surcharge — because that is
  // where yelo-userback applies it: the surcharge is already inside the netto
  // its price service returns before the percentage multiplies it.
  if (specialPercent) netto = netto * (1 + Number(specialPercent) / 100);

  return netto;
};
